import { useState } from 'react';

import Button from '../Input2/Button';
import Radio from '../Input2/Radio';
import Popup from '../Popup/Popup';

export interface ChangeDialNumberPopupProps {
  contactName?: string;
  /** The contact's currently pinned from-number (null when nothing is pinned yet). */
  currentNumber: string | null;
  /** The rep's assigned numbers (E.164) — the only values the contact can be re-pinned to. */
  bucketNumbers: string[];
  saving?: boolean;
  /** Called once with the picked number when the rep confirms. */
  onConfirm: (phoneNumber: string) => void;
  onClose: () => void;
}

/**
 * The deliberate confirmation behind the dialer's "Change" affordance. Left alone, a contact's
 * from-number moves through the rep's numbers while calls go unanswered and settles for good once
 * one connects. Choosing one here overrides that and fixes the number permanently, so it's meant
 * to be a considered action (e.g. the number died) — hence a popup with explicit copy and a
 * confirm button instead of an inline dropdown.
 */
export function ChangeDialNumberPopup({
  contactName,
  currentNumber,
  bucketNumbers,
  saving,
  onConfirm,
  onClose,
}: ChangeDialNumberPopupProps) {
  const [picked, setPicked] = useState<string | null>(null);
  const canConfirm = picked != null && picked !== currentNumber;

  const handleConfirm = () => {
    if (picked == null || picked === currentNumber) return;
    onConfirm(picked);
  };

  return (
    <Popup onClose={onClose} className="w-96">
      <span className="text-base font-semibold" data-test="change-dial-number-title">
        Change calling number
      </span>
      <p className="text-sm text-gray-600 dark:text-gray-300">
        Calls to {contactName ?? 'this contact'} move to a different number each time they go unanswered, then stay on
        whichever one they answer. Picking a number here stops that and uses it for all future calls to this contact.
      </p>
      <Radio
        className="mx-auto text-left"
        name="dial-number"
        disabled={saving}
        options={bucketNumbers.map((n) => ({ value: n, label: n === currentNumber ? `${n} (current)` : n }))}
        value={picked ?? currentNumber ?? undefined}
        onChange={setPicked}
        data-test="dial-number-options"
      />
      <div className="flex items-center justify-center gap-2">
        <Button tone="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          color="blue"
          // Also disabled while saving — `status` alone doesn't block a double-submit click.
          disabled={!canConfirm || saving}
          status={saving ? 'updating' : undefined}
          onClick={handleConfirm}
          data-test="confirm-dial-number">
          Use this number
        </Button>
      </div>
    </Popup>
  );
}
